export class GenerativeMusicEngine {
  private ctx: AudioContext | null = null;
  private isRunning: boolean = false;
  private isMuted: boolean = false;
  private masterGain: GainNode | null = null;
  private limiter: DynamicsCompressorNode | null = null;

  // Clock & Scheduling
  private nextNoteTime: number = 0;
  private timerId: number | null = null;
  private tempoBpm: number = 76;
  private currentStep: number = 0;
  private lookAheadMs: number = 50;
  private scheduleAheadSec: number = 0.2;

  // Musical Scales (D Major Pentatonic frequencies in Hz)
  // D3, E3, F#3, A3, B3, D4, E4, F#4, A4, B4, D5, E5, F#5, A5
  private pentatonicScale: number[] = [
    146.83, 164.81, 185.0, 220.0, 246.94,
    293.66, 329.63, 369.99, 440.0, 493.88,
    587.33, 659.26, 739.99, 880.0
  ];

  // Mood adjustments
  private moodWeather: 'sunny' | 'rain' | 'snow' = 'sunny';
  private moodTime: number = 1; // 0..5

  constructor() {
    // AudioContext will be initialized on user gesture
  }

  public init(): void {
    if (this.ctx) return;
    const AudioCtx = window.AudioContext || (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext;
    this.ctx = new AudioCtx();

    // Limiter to guarantee zero clipping
    this.limiter = this.ctx.createDynamicsCompressor();
    this.limiter.threshold.setValueAtTime(-3, this.ctx.currentTime);
    this.limiter.knee.setValueAtTime(6, this.ctx.currentTime);
    this.limiter.ratio.setValueAtTime(12, this.ctx.currentTime);
    this.limiter.attack.setValueAtTime(0.003, this.ctx.currentTime);
    this.limiter.release.setValueAtTime(0.25, this.ctx.currentTime);

    this.masterGain = this.ctx.createGain();
    this.masterGain.gain.setValueAtTime(0.65, this.ctx.currentTime);

    this.masterGain.connect(this.limiter);
    this.limiter.connect(this.ctx.destination);
  }

  public setVolume(volume0to1: number): void {
    if (!this.masterGain || !this.ctx) return;
    const target = this.isMuted ? 0 : Math.max(0, Math.min(1, volume0to1));
    this.masterGain.gain.setTargetAtTime(target * 0.7, this.ctx.currentTime, 0.05);
  }

  public setMute(muted: boolean): void {
    this.isMuted = muted;
    if (this.masterGain && this.ctx) {
      this.masterGain.gain.setTargetAtTime(muted ? 0 : 0.65, this.ctx.currentTime, 0.05);
    }
  }

  public setMood(timeOfDay: number, weather: 'sunny' | 'rain' | 'snow'): void {
    this.moodTime = timeOfDay;
    this.moodWeather = weather;

    // Adjust tempo based on mood
    if (timeOfDay === 5) {
      // Night is slower & dreamy
      this.tempoBpm = 68;
    } else if (timeOfDay === 3 || timeOfDay === 0) {
      // Dawn/Golden hour
      this.tempoBpm = 74;
    } else {
      this.tempoBpm = 82;
    }
  }

  public start(): void {
    this.init();
    if (!this.ctx) return;

    if (this.ctx.state === 'suspended') {
      this.ctx.resume();
    }

    if (this.isRunning) return;
    this.isRunning = true;
    this.nextNoteTime = this.ctx.currentTime + 0.1;
    this.currentStep = 0;
    this.scheduler();
  }

  public stop(): void {
    this.isRunning = false;
    if (this.timerId !== null) {
      window.clearTimeout(this.timerId);
      this.timerId = null;
    }
  }

  private scheduler = (): void => {
    if (!this.isRunning || !this.ctx) return;

    while (this.nextNoteTime < this.ctx.currentTime + this.scheduleAheadSec) {
      this.schedulePattern(this.currentStep, this.nextNoteTime);
      const secondsPerBeat = 60.0 / this.tempoBpm;
      const secondsPerSixteenth = secondsPerBeat / 4.0;
      this.nextNoteTime += secondsPerSixteenth;
      this.currentStep = (this.currentStep + 1) % 64; // 4-bar phrase
    }

    this.timerId = window.setTimeout(this.scheduler, this.lookAheadMs);
  };

  private schedulePattern(step: number, time: number): void {
    if (!this.ctx || !this.masterGain) return;

    const bar = Math.floor(step / 16);
    const beat = Math.floor((step % 16) / 4);
    const sixteenth = step % 4;

    // 1. Warm Ambient Drone / Pad (Triggered every 16 steps = every bar)
    if (step % 16 === 0) {
      const padRootIndex = bar % 2 === 0 ? 0 : 3; // D or A root
      const rootFreq = this.pentatonicScale[padRootIndex];
      const fifthFreq = this.pentatonicScale[padRootIndex + (this.moodTime === 5 ? 2 : 3)];
      this.playPadVoice(rootFreq, time, 4.2);
      this.playPadVoice(fifthFreq, time, 4.2);
    }

    // 2. Guzheng / Plucked String Melody
    // Pentatonic arpeggio & ornamentation
    const shouldPluck = (step % 4 === 0) || (step % 4 === 2 && Math.random() > 0.4) || (step % 4 === 3 && Math.random() > 0.7);
    if (shouldPluck) {
      // Pick note biased towards middle register
      const noteIndices = [4, 5, 6, 7, 8, 9, 10, 11];
      const noteIndex = noteIndices[Math.floor(Math.random() * noteIndices.length)];
      const freq = this.pentatonicScale[noteIndex];
      const duration = 0.8 + Math.random() * 0.6;
      this.playPluckVoice(freq, time, duration);
    }

    // 3. Dizi / Bamboo Flute Melody (Slower, expressive legato lines)
    if (step % 8 === 0 && Math.random() > 0.3) {
      const fluteIndices = [7, 8, 9, 10, 11, 12];
      const noteIndex = fluteIndices[Math.floor(Math.random() * fluteIndices.length)];
      const freq = this.pentatonicScale[noteIndex];
      this.playFluteVoice(freq, time, 1.4);
    }

    // 4. Soft Woodblock / Clack on downbeats
    if (sixteenth === 0 && (beat === 0 || beat === 2)) {
      this.playWoodblock(time, beat === 0 ? 800 : 650);
    }
  }

  private playPluckVoice(freq: number, time: number, duration: number): void {
    if (!this.ctx || !this.masterGain) return;

    const osc = this.ctx.createOscillator();
    const gain = this.ctx.createGain();
    const filter = this.ctx.createBiquadFilter();

    osc.type = 'triangle';
    osc.frequency.setValueAtTime(freq, time);

    filter.type = 'lowpass';
    filter.frequency.setValueAtTime(freq * 4.5, time);
    filter.frequency.exponentialRampToValueAtTime(freq * 1.2, time + duration * 0.7);

    // Pluck amplitude envelope: quick attack, natural exponential decay
    const vol = 0.22;
    gain.gain.setValueAtTime(0.0001, time);
    gain.gain.linearRampToValueAtTime(vol, time + 0.008);
    gain.gain.exponentialRampToValueAtTime(0.0001, time + duration);

    osc.connect(filter);
    filter.connect(gain);
    gain.connect(this.masterGain);

    osc.start(time);
    osc.stop(time + duration);
  }

  private playFluteVoice(freq: number, time: number, duration: number): void {
    if (!this.ctx || !this.masterGain) return;

    const osc = this.ctx.createOscillator();
    const gain = this.ctx.createGain();
    const filter = this.ctx.createBiquadFilter();

    // Dizi vibrato LFO (5.2 Hz)
    const vibrato = this.ctx.createOscillator();
    const vibratoGain = this.ctx.createGain();
    vibrato.frequency.setValueAtTime(5.2, time);
    vibratoGain.gain.setValueAtTime(freq * 0.015, time); // Subtle pitch vibrato
    vibrato.connect(vibratoGain);
    vibratoGain.connect(osc.frequency);

    osc.type = 'sine';
    osc.frequency.setValueAtTime(freq, time);

    filter.type = 'bandpass';
    filter.frequency.setValueAtTime(freq * 1.5, time);
    filter.Q.setValueAtTime(1.8, time);

    // Flute breath envelope: soft 120ms attack, expressive sustain, smooth release
    const vol = this.moodWeather === 'rain' ? 0.12 : 0.16;
    gain.gain.setValueAtTime(0.0001, time);
    gain.gain.linearRampToValueAtTime(vol, time + 0.15);
    gain.gain.setValueAtTime(vol * 0.9, time + duration - 0.2);
    gain.gain.exponentialRampToValueAtTime(0.0001, time + duration);

    osc.connect(filter);
    filter.connect(gain);
    gain.connect(this.masterGain);

    vibrato.start(time);
    osc.start(time);
    vibrato.stop(time + duration);
    osc.stop(time + duration);
  }

  private playPadVoice(freq: number, time: number, duration: number): void {
    if (!this.ctx || !this.masterGain) return;

    const osc1 = this.ctx.createOscillator();
    const osc2 = this.ctx.createOscillator();
    const gain = this.ctx.createGain();
    const filter = this.ctx.createBiquadFilter();

    osc1.type = 'sine';
    osc2.type = 'triangle';
    osc1.frequency.setValueAtTime(freq, time);
    osc2.frequency.setValueAtTime(freq * 1.002, time); // slight chorus detune

    filter.type = 'lowpass';
    filter.frequency.setValueAtTime(450, time);

    const vol = 0.09;
    gain.gain.setValueAtTime(0.0001, time);
    gain.gain.linearRampToValueAtTime(vol, time + 1.2);
    gain.gain.setValueAtTime(vol * 0.8, time + duration - 1.0);
    gain.gain.exponentialRampToValueAtTime(0.0001, time + duration);

    osc1.connect(filter);
    osc2.connect(filter);
    filter.connect(gain);
    gain.connect(this.masterGain);

    osc1.start(time);
    osc2.start(time);
    osc1.stop(time + duration);
    osc2.stop(time + duration);
  }

  private playWoodblock(time: number, freq: number): void {
    if (!this.ctx || !this.masterGain) return;

    const osc = this.ctx.createOscillator();
    const gain = this.ctx.createGain();

    osc.type = 'sine';
    osc.frequency.setValueAtTime(freq, time);
    osc.frequency.exponentialRampToValueAtTime(freq * 0.4, time + 0.05);

    gain.gain.setValueAtTime(0.08, time);
    gain.gain.exponentialRampToValueAtTime(0.0001, time + 0.05);

    osc.connect(gain);
    gain.connect(this.masterGain);

    osc.start(time);
    osc.stop(time + 0.05);
  }

  public dispose(): void {
    this.stop();
    if (this.ctx && this.ctx.state !== 'closed') {
      this.ctx.close();
      this.ctx = null;
    }
  }
}
